/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import {
  handleGetUpdatedAccountGroup,
  handleSetUpdatedAccountGroup,
  handlePackDynamicAccountGroup,
} from "./packs.utils";
import {
  uiHelpersState,
  selectBucketsRemainingCount,
} from "@/global/store/data/uiHelpers";
import {
  set,
  selectPacks,
  selectSelectedPack,
  updatePackBucketMax,
  updateOpenedPackBucket,
  setPackBucketMaxCountReached,
} from "@/global/store/data/purchaseSeats/packs";
import { pluralize } from "@/global/utils/utils";
import {
  selectEventsSelected,
  updateEventsSelected,
  updateEventsSelectedWithoutSort,
} from "@/global/store/data/purchaseSeats/events";
import { usePacksModal } from "../modals/PacksModal";
import { selectUser } from "@/global/store/data/user";
import {
  cartManagerState,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppDispatch, useAppStore } from "@/global/store";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import { PacksType } from "@/types";
import type { EventType } from "@/global/store/data/types";

export function usePacks() {
  const dispatch = useAppDispatch();
  const { sm } = useCssMediaQuery("down");
  const { packs, teamSchema, languages, uiConfigs } = useTeamConfig();

  /** states */
  const packsModal = usePacksModal();
  const user = useAppStore(selectUser);
  const packsList = useAppStore(selectPacks);
  const isPacksEnabled = packs?.enabled || false;
  const cartState = useAppStore(selectCartManager);
  const selectedPack = useAppStore(selectSelectedPack);
  const selectedEvents = useAppStore(selectEventsSelected);
  const dontOpenPackModal = packs?.dontOpenPackModal ?? false;
  const isPackBucketsEnabledAndMobile = (sm && isPacksEnabled) || false;
  const packBucketsRemainingCount = useAppStore(selectBucketsRemainingCount);
  const progressBarGameText = languages?.purchaseSeats?.progressBarGameText || "game"; // prettier-ignore
  const progressBarPackText = languages?.purchaseSeats?.progressBarPackText || "pack"; // prettier-ignore
  const confirmEventSelectionLossTitle = languages?.purchaseSeats?.confirmEventSelectionLossTitle || "Update Your Pack"; // prettier-ignore

  /**
   * Function to select a pack based on the id and updates the state
   * @param {number} id id to identify the selected pack.
   */
  // prettier-ignore
  function handleSelectPack(id: number) {
    if (!isPacksEnabled) return;

    if (selectedPack && selectedPack.id > id) {
      const packs = packsList.map((pack) => ({
        ...pack,
        selected: pack.id === id,
        buckets: pack.buckets.map((bucket, idx) => {
          const isOpened = pack.buckets[0].id === bucket.id;
          const idxMinZero = pack.buckets[idx - 1]?.min == 0;
          return {
            ...bucket,
            opened: idxMinZero || isOpened,
            disabled: idxMinZero ? false : !isOpened,
            isMaxCountReached: false,
          };
        }),
      }));
      dispatch(set(packs));
      selectedEvents.forEach((event) => handleEventSelection(event));
    }

    if (selectedPack && selectedPack.id < id) {
      packsList.forEach((pack) => {
        if (pack.id == id) {
          dispatch(set(packsList.map((pack) => ({ ...pack, selected: pack.id === id }))));
          if (selectedEvents.length > 0) {
            pack.buckets.forEach((bucket) => {
              const selectedEventsOnBucket = selectedEvents.filter((event) => event.bucketId === bucket.id).length;
              dispatch(updateOpenedPackBucket({ packId: pack.id, opened: packs?.independentPacks ? true : selectedEventsOnBucket < bucket.max, disabled: false, bucketId: bucket.id }));
              dispatch(setPackBucketMaxCountReached({ bucketId: bucket.id, isMaxCountReached: packs?.independentPacks ? false : bucket.min === 0 ? false : selectedEventsOnBucket == bucket.min, packId: pack.id }));
            });

            // unselecting events that are not part of pack
            const selectedPackEventIds = pack.buckets.flatMap(bucket => bucket.events!.map(event => event.eventCode));
            const filteredSelectedEventsNotPartOfPack = selectedEvents.filter(event => !selectedPackEventIds.includes(event.eventCode));
            filteredSelectedEventsNotPartOfPack.forEach(event => handleEventSelection(event));
          }
        }
      });
    }
    
    const pack = packsList.find((pack) => pack.id === id);
    if (pack) {
      if (pack?.dynamicAccountGroup && user?.accountGroupIds?.length > 0) handlePackDynamicAccountGroup(user, pack, teamSchema);
      else {
        if (String(pack?.updatedAccountGroup)) handleSetUpdatedAccountGroup([String(pack.updatedAccountGroup)], teamSchema);
      }
    }
  }

  /**
   * Function to handle event selection
   * @param {EventType} event
   */
  // prettier-ignore
  function handleEventSelection(event: EventType) {
    if (uiConfigs?.findGames?.setEventsWithoutSorting) {
      dispatch(updateEventsSelectedWithoutSort(event));
    } else dispatch(updateEventsSelected(event));

    if (cartState.cart.some((itm) => itm.eventCode == event.eventCode)) {
      dispatch(cartManagerState.actions.removeEvent({ eventCode: event.eventCode }));
    }
  }

  /**
   * Function to get updated min quantity required for bucket based on reducer
   * @param bucketId
   */
  // prettier-ignore
  function handleGetBucketUpdatedMinMax(bucketId: number): { min: number; max: number } {
    if (!isPacksEnabled || !selectedPack) return { min: 0, max: 0 };
    return selectedPack.buckets.reduce((acc, bucket) => {
      if (bucket.id === bucketId) {
        return {
          min: bucket.min,
          max: bucket.max,
        };
      }
      return acc;
    }, { min: 0, max: 0 });
  }

  function handleGetNextPackBucketById(bucketId: number) {
    if (!isPacksEnabled || !selectedPack?.selected) return;
    const nextBucket = selectedPack.buckets.find((bucket) => bucket.id === bucketId + 1); // prettier-ignore
    if (nextBucket) return nextBucket;
  }

  /**
   * Select event function if packs are enabled
   * @param {EventType} event
   * @param {number} bucketId
   */
  // prettier-ignore
  function handleSelectEventForPacks(event: EventType, bucketId: number) {
    if (!isPacksEnabled || !selectedPack?.selected) return;

    handleEventSelection(event);

    const selectedEventsQuantity: number = selectedPack.priritizePackAmountForEventCount
      ? [...selectedEvents, event].length
      : [...selectedEvents, event].filter((event) => event.bucketId === bucketId).length;

    const packId = selectedPack.id;
    const bucketQuantity = handleGetBucketUpdatedMinMax(bucketId);

    const bucket = selectedPack.buckets.find((bucket) => bucket.id === bucketId);

    if (selectedEventsQuantity == bucketQuantity.min || (bucket)?.closeWhenHitMax && selectedEventsQuantity == bucketQuantity.max) {
      handleOpenPackBucket(bucketId);

      if (selectedPack.scrollToTopWhenSelecting) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }

    // if selected events is equal to bucket max, it means the bucket is full and we need to disable it
    if (selectedEventsQuantity == bucketQuantity.max) {
      dispatch(setPackBucketMaxCountReached({ bucketId, isMaxCountReached: true, packId }));
      if (bucket?.scrollToTopWhenHitMax) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }

    // if values are the same, it means the next bucket should get enabled
    const nextBucket = handleGetNextPackBucketById(bucketId);
    if (selectedEventsQuantity == bucketQuantity.min || selectedPack.priritizePackAmountForEventCount) {
      if (nextBucket) {
        const { id, bucketTagId } = nextBucket;
        dispatch(
          updateOpenedPackBucket({
            packId,
            opened: true,
            disabled: false,
            bucketId: id,
          })
        );

        if (bucketTagId && isPackBucketsEnabledAndMobile) {
          window.scrollTo({ top: 500, behavior: "smooth" });
        }
      }
    }

    // finding next bucket, checking if it has shouldUseMaxAmountOfBucketId and update max property
    if (nextBucket && nextBucket.shouldUseMaxAmountOfBucketId == bucketId) {
      const { payload } = dispatch(updatePackBucketMax({ packId, bucketId: nextBucket.id, max: selectedEventsQuantity }));
      const selectedEventsInNextBucket = selectedEvents.filter((events) => events.bucketId === nextBucket.id).length;
      if (payload.max > selectedEventsInNextBucket) {
        dispatch(setPackBucketMaxCountReached({ packId, bucketId: nextBucket.id, isMaxCountReached: false }));
      }
    }

    if (selectedPack.priritizePackAmountForEventCount && selectedEventsQuantity === selectedPack.amount) {
      selectedPack.buckets.forEach((bucket) => dispatch(setPackBucketMaxCountReached({ packId, bucketId: bucket.id, isMaxCountReached: true })));
    }
  }

  /**
   * Deselect event function if packs are enabled
   * @param {EventType} event
   * @param {number} bucketId
   */
  // prettier-ignore
  function handleDeselectEventForPacks(event: EventType, bucketId: number) {
    if (!isPacksEnabled || !selectedPack?.selected) return;

    handleEventSelection(event);

    const selectedEventsQuantity: number = selectedEvents.filter((selectedEvent) =>
      selectedEvent.bucketId === bucketId &&
      selectedEvent.eventCode != event.eventCode
    ).length;

    const packId = selectedPack.id;
    const bucketQuantity = handleGetBucketUpdatedMinMax(bucketId);

    if (selectedEventsQuantity < bucketQuantity.min) {
      // loop into subsequent buckets and disable them
      for (let i = bucketId; i < selectedPack.buckets.length; i++) {
        const bucket = selectedPack.buckets[i];
        dispatch(updateOpenedPackBucket({ bucketId: bucket.id, opened: false, disabled: true, packId }));
        dispatch(setPackBucketMaxCountReached({ bucketId: bucket.id, isMaxCountReached: false, packId }));
        // unselect events from subsequent buckets
        selectedEvents.forEach((selectedEvent) => {
          if (selectedEvent.bucketId === bucket.id) handleEventSelection(selectedEvent);
        });
      }
    }

    // if selected events quantity is less than bucket max, enable the bucket again
    if (selectedEventsQuantity < bucketQuantity.max)
      dispatch(setPackBucketMaxCountReached({ bucketId, isMaxCountReached: false, packId }));

    const nextBucket = handleGetNextPackBucketById(bucketId);
    if (nextBucket && nextBucket.shouldUseMaxAmountOfBucketId == bucketId && selectedEventsQuantity > 0) {
      const { payload } = dispatch(updatePackBucketMax({ bucketId: nextBucket.id, max: selectedEventsQuantity, packId }));
      // if selected events quantity in next bucket is greater max property, we need to remove one event
      const selectedEventsInNextBucket = selectedEvents.filter((selectedEvent) => selectedEvent.bucketId === nextBucket.id);
      if (selectedEventsInNextBucket.length > payload.max) {
        const eventToRemove = selectedEventsInNextBucket[selectedEventsInNextBucket.length - 1];
        if (eventToRemove) {
          handleEventSelection(eventToRemove);
          dispatch(setPackBucketMaxCountReached({ bucketId: nextBucket.id, isMaxCountReached: true, packId }));
        } 
      } else if (payload.max == selectedEventsInNextBucket.length) {
        dispatch(setPackBucketMaxCountReached({ bucketId: nextBucket.id, isMaxCountReached: true, packId }));
      }
    }

    if (selectedPack.priritizePackAmountForEventCount && selectedEventsQuantity < selectedPack.amount) {
      selectedPack.buckets.forEach((bucket) => dispatch(setPackBucketMaxCountReached({ packId, bucketId: bucket.id, isMaxCountReached: false })));
    }
  }

  /**
   * Function to open bucket
   * @param {number} bucketId
   */
  // prettier-ignore
  function handleOpenPackBucket(bucketId: number) {
    if (!isPacksEnabled || !selectedPack?.buckets) return;
    const packId = selectedPack.id;
    if (selectedPack.buckets.some((bucket) => bucket.id == bucketId && bucket.opened)) {
      const dontCloseWhenHitMin = selectedPack.buckets.find((bucket) => bucket.id == bucketId)?.dontCloseWhenHitMin || false;
      return dispatch(updateOpenedPackBucket({ bucketId, opened: dontCloseWhenHitMin || false, disabled: false, packId }));
    }
    dispatch(updateOpenedPackBucket({ bucketId, opened: true, disabled: false, packId }));
  }

  /**
   * Function responsible to handle event selection remaining count based on pack bucket id
   */
  // prettier-ignore
  function handleEventSelectionRemainingCount() {
    if (!isPacksEnabled || !selectedPack || !selectedPack.selected) return;
    const remainingCount = selectedPack.buckets.map((bucket) => {
      const selectedEventsQuantity = selectedPack.priritizePackAmountForEventCount 
        ? selectedEvents.length 
        : selectedEvents.filter((event) => event.bucketId === bucket.id).length;
      const bucketQuantity = handleGetBucketUpdatedMinMax(bucket.id as number);
      const remaining = selectedPack.priritizePackAmountForEventCount
        ? selectedPack.amount - selectedEventsQuantity
        : bucketQuantity.min > 0
        ? bucketQuantity.min - selectedEventsQuantity
        : bucketQuantity.max - selectedEventsQuantity;
      return {
        remaining,
        id: bucket.id as number,
        isComplete: remaining <= 0,
      };
    });
  
    dispatch(uiHelpersState.actions.setBucketRemainingCount(remainingCount));
  }

  // prettier-ignore
  function getSelectedEventsPerPackBucket(): number {
    if (!isPacksEnabled || !selectedPack || !selectedPack.selected) return 0;
    return selectedPack.buckets.reduce((acc, bucket) => {
      const selectedEventsQuantity = selectedPack.priritizePackAmountForEventCount
        ? selectedEvents.length
        : selectedEvents.filter((event) => event.bucketId === bucket.id).length;
      return acc + (selectedEventsQuantity > bucket.min ? bucket.min : selectedEventsQuantity);
    }, 0);
  }

  // prettier-ignore
  function getPackBucketMessage(): string {
    if (!isPacksEnabled || !selectedPack || !selectedPack.selected) return "";

    const packBucketsRemaining = packBucketsRemainingCount.find((bucket) => !bucket.isComplete);

    if (packBucketsRemaining) {
      const { id } = packBucketsRemaining;

      const customProgressBarMessage = selectedPack?.customProgressBarLanguage;
      if (customProgressBarMessage && customProgressBarMessage.length > 0) {
        const customMessage = customProgressBarMessage.find((msg) => msg.selectedEventCount === selectedEvents.length);
        if (customMessage) return customMessage.progressBarText;
      }
      
      const prioritizeMinGameSelectionCount = selectedPack.priritizePackAmountForEventCount || false;
      if (prioritizeMinGameSelectionCount) {
        const remaningGameCount = selectedPack.amount - selectedEvents.length;
        const bucketRemainingLanguage = (selectedPack.buckets.find((bucket) => bucket.id === id)?.remainingBucketLanguage);
        return bucketRemainingLanguage || `Add ${remaningGameCount} more ${pluralize(progressBarGameText, remaningGameCount)} to complete your ${progressBarPackText}.`;
      }
  
      if (!prioritizeMinGameSelectionCount) {
        const remainingBuckets = packBucketsRemainingCount.reduce((acc, bucket) => {
          if (!bucket.isComplete) return { remaining: acc.remaining + bucket.remaining };
          return acc;
        }, { remaining: 0 });
        if (remainingBuckets) return `Add ${remainingBuckets.remaining} more ${pluralize(progressBarGameText, remainingBuckets.remaining)} to complete your ${progressBarPackText}.`;
      }
    }

    return ""; // No open buckets
  }

  // prettier-ignore
  function getPackBucketStaticMin(): number {
    if (!isPacksEnabled || !selectedPack || !selectedPack.selected) return 0;
    return selectedPack.buckets.reduce((acc, bucket) => {
      if (selectedPack.priritizePackAmountForEventCount)
        return selectedPack.amount;

      if (bucket.shouldUseMinAmountOfBucketId) {
        const bucketMin = selectedPack.buckets.find((itm) => itm.id == bucket.shouldUseMinAmountOfBucketId)?.min;
        return acc + (bucketMin || 0);
      }
      return acc + bucket.min;
    }, 0);
  }

  // helper function to check if ther user selected a pack logged out with dynamic account group
  // and moved to choose seats page, if so, we need to update the updatedAccountGroup
  // prettier-ignore
  function handleCheckPackDynamicAccountGroup() {
    if (!isPacksEnabled) return;
    const updatedAccountGroup = handleGetUpdatedAccountGroup(teamSchema);
    if (updatedAccountGroup === undefined) return;
    const selectedPack = packs?.list?.find((pack) => pack.updatedAccountGroup?.toString() === updatedAccountGroup.toString());
    if (selectedPack && selectedPack?.dynamicAccountGroup && user?.accountGroupIds?.length > 0) {
      handlePackDynamicAccountGroup(user, selectedPack, teamSchema);
    }
  }

  // prettier-ignore
  function getProgressBarSuccessLanguage(): string {
    if (selectedPack?.customProgressBarLanguage) {
      const progressBarText = selectedPack?.customProgressBarLanguage.find((msg) => msg.selectedEventCount === selectedEvents.length)?.progressBarText;
      if (progressBarText) return progressBarText;
    }
    if (selectedPack?.successMessage) return selectedPack.successMessage;
    return "You're good to go! Add more games or checkout now!";
  }

  function handleCheckShouldOpenPacksModal(): boolean {
    if (
      isPacksEnabled &&
      selectedPack?.selected &&
      !!packs &&
      (packs as PacksType).list
        .map((itm) => itm.id)
        .slice(0, -1)
        .includes(selectedPack.id) &&
      !packsModal.hasModalOpened &&
      !dontOpenPackModal
    ) {
      return true;
    }
    return false;
  }

  const [packBucketMessage, setPackBucketMessage] = useState(getPackBucketMessage()); // prettier-ignore
  const [selectedEventsPerPackBucket, setSelectEventsPerPackBucket] = useState(getSelectedEventsPerPackBucket()); // prettier-ignore
  const [packBucketMin, setPackBucketMin] = useState(getPackBucketStaticMin());

  useEffect(() => {
    if (isPacksEnabled) {
      setPackBucketMin(getPackBucketStaticMin());
      setPackBucketMessage(getPackBucketMessage());
      setSelectEventsPerPackBucket(getSelectedEventsPerPackBucket());
    }
  }, [isPacksEnabled, packBucketsRemainingCount]);

  useEffect(() => {
    if (isPacksEnabled) handleEventSelectionRemainingCount();
  }, [isPacksEnabled, selectedEvents, selectedPack]);

  // automatically hits the continue button after selecting the last pack based on continue
  useEffect(() => {
    // if selected events quantity is equal to last pack amount, we need to confirm the pack
    if (isPacksEnabled && selectedPack && packs) {
      const totalSelectedEvents = selectedEvents.length;
      const lastPack = packs.list[packs.list.length - 1];
      // prettier-ignore
      if (lastPack && lastPack.id === selectedPack.id && lastPack.amount === totalSelectedEvents) {
        if (packBucketsRemainingCount.every((itm) => itm.isComplete)) {
          const cartConfirmBtn = document.getElementById("confirm-game-selection-btn");
          if(packs?.autoContinueOnLastPack) cartConfirmBtn?.click();
        }
      }
    }
  }, [selectedPack, selectedEvents, isPacksEnabled, packBucketsRemainingCount]);

  return {
    packsModal,
    selectedPack,
    packBucketMin,
    isPacksEnabled,
    handleSelectPack,
    dontOpenPackModal,
    packBucketMessage,
    packs: packs || {},
    progressBarPackText,
    handleOpenPackBucket,
    handleSelectEventForPacks,
    packBucketsRemainingCount,
    handleDeselectEventForPacks,
    selectedEventsPerPackBucket,
    getProgressBarSuccessLanguage,
    isPackBucketsEnabledAndMobile,
    confirmEventSelectionLossTitle,
    handleCheckShouldOpenPacksModal,
    handleCheckPackDynamicAccountGroup,
  };
}
